import React, { useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Button, TextField, InputAdornment,
  Avatar, Grid, Card, CardContent, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, FormControl, InputLabel, Select,
  MenuItem, Snackbar, Alert, Divider, Tooltip
} from '@mui/material';
import { Search, Add, Edit, Email, Phone, Close, Download } from '@mui/icons-material';

const departments = ['Administration', 'Faculty', 'Support Staff', 'Counseling', 'Athletics'];

const mockStaff = [
  { id: 'EMP-001', name: 'Dr. James Johnson', role: 'Principal', department: 'Administration', email: 'principal@era.edu', phone: '(512) 555-0101', joinDate: '2018-08-01', status: 'active' },
  { id: 'EMP-002', name: 'Mrs. Linda Chen', role: 'Vice Principal', department: 'Administration', email: 'vp@era.edu', phone: '(512) 555-0102', joinDate: '2019-08-01', status: 'active' },
  { id: 'EMP-003', name: 'Mrs. Emily Smith', role: '5th Grade Math Teacher', department: 'Faculty', email: 'smith@era.edu', phone: '(512) 555-0110', joinDate: '2020-08-15', status: 'active' },
  { id: 'EMP-004', name: 'Mr. Robert Davis', role: '8th Grade Math Teacher', department: 'Faculty', email: 'davis@era.edu', phone: '(512) 555-0111', joinDate: '2021-08-15', status: 'active' },
  { id: 'EMP-005', name: 'Mrs. Patricia Williams', role: '7th Grade English Teacher', department: 'Faculty', email: 'williams@era.edu', phone: '(512) 555-0112', joinDate: '2019-08-15', status: 'active' },
  { id: 'EMP-006', name: 'Mr. Carlos Garcia', role: 'Science Teacher', department: 'Faculty', email: 'garcia@era.edu', phone: '(512) 555-0113', joinDate: '2022-08-15', status: 'active' },
  { id: 'EMP-007', name: 'Pastor Samuel Lee', role: 'Bible Studies Teacher / Chaplain', department: 'Faculty', email: 'pastor.lee@era.edu', phone: '(512) 555-0114', joinDate: '2018-08-15', status: 'active' },
  { id: 'EMP-008', name: 'Coach Mike Adams', role: 'Physical Education', department: 'Athletics', email: 'adams@era.edu', phone: '(512) 555-0115', joinDate: '2020-08-15', status: 'active' },
  { id: 'EMP-009', name: 'Mrs. Sharon Green', role: 'School Counselor', department: 'Counseling', email: 'counselor@era.edu', phone: '(512) 555-0120', joinDate: '2021-01-10', status: 'active' },
  { id: 'EMP-010', name: 'Ms. Angela Nguyen', role: 'School Nurse', department: 'Support Staff', email: 'nurse@era.edu', phone: '(512) 555-0125', joinDate: '2022-01-05', status: 'active' },
  { id: 'EMP-011', name: 'Mr. Kevin Park', role: 'IT Administrator', department: 'Support Staff', email: 'it@era.edu', phone: '(512) 555-0130', joinDate: '2023-03-15', status: 'active' },
  { id: 'EMP-012', name: 'Mrs. Diana Carter', role: 'Administrative Assistant', department: 'Administration', email: 'admin@era.edu', phone: '(512) 555-0103', joinDate: '2019-06-01', status: 'active' },
  { id: 'EMP-013', name: 'Mr. Thomas Brown', role: '3rd Grade Teacher', department: 'Faculty', email: 'brown@era.edu', phone: '(512) 555-0116', joinDate: '2020-08-15', status: 'on-leave' },
];

const deptColors = {
  Administration: 'primary',
  Faculty: 'success',
  'Support Staff': 'info',
  Counseling: 'warning',
  Athletics: 'secondary',
};

const StaffAdmin = () => {
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [addDialog, setAddDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const filtered = mockStaff.filter(s => {
    const sm = !search || `${s.name} ${s.role} ${s.email}`.toLowerCase().includes(search.toLowerCase());
    const dm = deptFilter === 'all' || s.department === deptFilter;
    return sm && dm;
  });

  const countByDept = departments.reduce((acc, d) => ({ ...acc, [d]: mockStaff.filter(s => s.department === d).length }), {});

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Staff Directory</Typography>
          <Typography variant="body2" color="text.secondary">{mockStaff.length} staff members</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<Download />}>Export</Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => setAddDialog(true)}>Add Staff Member</Button>
        </Box>
      </Box>

      {/* Dept Summary */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {departments.map(dept => (
          <Grid item xs key={dept}>
            <Card elevation={1} sx={{ borderRadius: 2, cursor: 'pointer', border: '2px solid', borderColor: deptFilter === dept ? 'primary.main' : 'transparent' }}
              onClick={() => setDeptFilter(deptFilter === dept ? 'all' : dept)}>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{countByDept[dept]}</Typography>
                <Typography variant="caption" color="text.secondary">{dept}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper elevation={2} sx={{ borderRadius: 2 }}>
        <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search staff..."
            value={search} onChange={e => setSearch(e.target.value)}
            size="small" sx={{ minWidth: 260 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Department</InputLabel>
            <Select value={deptFilter} label="Department" onChange={e => setDeptFilter(e.target.value)}>
              <MenuItem value="all">All Departments</MenuItem>
              {departments.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 600 }}>Staff Member</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Department</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Contact</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Joined</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map(s => (
                <TableRow key={s.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.light' }}>
                        {s.name.split(' ').map(n => n[0]).slice(-2).join('')}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{s.id}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{s.role}</TableCell>
                  <TableCell><Chip label={s.department} color={deptColors[s.department]} size="small" /></TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="caption" display="block">{s.email}</Typography>
                      <Typography variant="caption" color="text.secondary">{s.phone}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{s.joinDate}</TableCell>
                  <TableCell>
                    <Chip label={s.status === 'active' ? 'Active' : 'On Leave'} color={s.status === 'active' ? 'success' : 'warning'} size="small" />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="View Profile"><IconButton size="small" onClick={() => setSelected(s)}><Edit fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Send Email"><IconButton size="small" onClick={() => setSnackbar({ open: true, message: `Email composed to ${s.name}` })}><Email fontSize="small" /></IconButton></Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Staff Profile Dialog */}
      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        {selected && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ width: 48, height: 48, bgcolor: 'primary.main', fontSize: '1.1rem' }}>
                  {selected.name.split(' ').map(n => n[0]).slice(-2).join('')}
                </Avatar>
                <Box>
                  <Typography variant="h6">{selected.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{selected.id}</Typography>
                </Box>
              </Box>
              <IconButton onClick={() => setSelected(null)} size="small"><Close /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                {[
                  ['Role', selected.role],
                  ['Department', selected.department],
                  ['Email', selected.email],
                  ['Phone', selected.phone],
                  ['Join Date', selected.joinDate],
                  ['Status', selected.status === 'active' ? 'Active' : 'On Leave'],
                ].map(([label, val]) => (
                  <Grid item xs={6} key={label}>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{val}</Typography>
                  </Grid>
                ))}
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
              <Button onClick={() => setSelected(null)}>Close</Button>
              <Button variant="outlined" startIcon={<Email />} onClick={() => { setSelected(null); setSnackbar({ open: true, message: `Email composed to ${selected.name}` }); }}>Send Email</Button>
              <Button variant="contained" startIcon={<Edit />}>Edit Profile</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Add Staff Dialog */}
      <Dialog open={addDialog} onClose={() => setAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
          Add Staff Member
          <IconButton size="small" onClick={() => setAddDialog(false)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={6}><TextField label="First Name" fullWidth required /></Grid>
            <Grid item xs={6}><TextField label="Last Name" fullWidth required /></Grid>
            <Grid item xs={12}><TextField label="Job Title / Role" fullWidth required /></Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Department</InputLabel>
                <Select label="Department">
                  {departments.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}><TextField label="Email" type="email" fullWidth required /></Grid>
            <Grid item xs={6}><TextField label="Phone" fullWidth /></Grid>
            <Grid item xs={6}><TextField type="date" label="Start Date" fullWidth InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>System Role</InputLabel>
                <Select label="System Role" defaultValue="teacher">
                  <MenuItem value="admin">Administrator</MenuItem>
                  <MenuItem value="teacher">Teacher</MenuItem>
                  <MenuItem value="staff">Support Staff</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setAddDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => { setAddDialog(false); setSnackbar({ open: true, message: 'Staff member added — invitation email sent' }); }}>Add Staff Member</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setSnackbar(p => ({ ...p, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default StaffAdmin;
